import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  GET_CART,
  ADD_TO_CART,
  UPDATE_CART_ITEM,
  REMOVE_FROM_CART,
} from '../graphql/operations'
import { useAuth } from '../auth/AuthContext'
import { getGuestId } from './guest'

// One cart abstraction for both shoppers and guests. The only difference
// is identity: a signed-in shopper's requests carry the JWT (Apollo's
// authLink adds it), so we OMIT guestToken; a guest carries the shared
// anonymous id (see lib/guest) as the guestToken. Using that same id for
// the voice session is what lets the avatar add to THIS cart server-side.
// The components never have to care which is which.

export interface CartLine {
  id: string
  quantity: number
  totalPrice: number
  product: { id: string; title: string; imageUrls: string[] | null; currency: string }
  productVariant: { id: string; title: string; price: number; option1: string | null }
}

interface CartState {
  items: CartLine[]
  count: number
  total: number
  currency: string
  loading: boolean
  add: (variantId: string, quantity: number) => Promise<{ ok: boolean; error?: string }>
  updateQty: (cartItemId: string, quantity: number) => Promise<void>
  remove: (cartItemId: string) => Promise<void>
  refresh: () => Promise<void>
  /** The guest cart token, when shopping anonymously (needed at checkout). */
  guestToken: string | null
}

const CartContext = createContext<CartState | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient()
  const { token: authToken } = useAuth()
  const [items, setItems] = useState<CartLine[]>([])
  const [loading, setLoading] = useState(false)
  // Signed-in → user cart (no guestToken). Guest → the shared anonymous id.
  const guestToken = authToken ? null : getGuestId()

  // Signed-in → user cart (no guestToken). Guest → cart keyed by the id.
  const tokenForRequest = useCallback(
    () => (authToken ? undefined : getGuestId()),
    [authToken],
  )

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await client.query({
        query: GET_CART,
        variables: { guestToken: tokenForRequest() },
        fetchPolicy: 'network-only',
      })
      setItems((data?.cart ?? []) as CartLine[])
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [client, tokenForRequest])

  // Refetch whenever identity flips (login / logout).
  useEffect(() => { void refresh() }, [authToken, refresh])

  const add = useCallback(
    async (variantId: string, quantity: number) => {
      const { data } = await client.mutate({
        mutation: ADD_TO_CART,
        variables: { productVariantId: variantId, quantity, guestToken: tokenForRequest() },
      })
      const payload = data?.addToCart
      const err = payload?.userErrors?.[0]?.message
      if (err) return { ok: false, error: err }
      // The guest token is the stable shared id we already sent, so there's
      // nothing to persist from the response — just refresh the basket.
      await refresh()
      return { ok: true }
    },
    [client, tokenForRequest, refresh],
  )

  const updateQty = useCallback(
    async (cartItemId: string, quantity: number) => {
      await client.mutate({
        mutation: UPDATE_CART_ITEM,
        variables: { cartItemId, quantity, guestToken: tokenForRequest() },
      })
      await refresh()
    },
    [client, tokenForRequest, refresh],
  )

  const remove = useCallback(
    async (cartItemId: string) => {
      await client.mutate({
        mutation: REMOVE_FROM_CART,
        variables: { cartItemId, guestToken: tokenForRequest() },
      })
      await refresh()
    },
    [client, tokenForRequest, refresh],
  )

  const value = useMemo<CartState>(() => {
    const count = items.reduce((n, i) => n + i.quantity, 0)
    const total = items.reduce((s, i) => s + i.totalPrice, 0)
    const currency = items[0]?.product.currency ?? 'GBP'
    return { items, count, total, currency, loading, add, updateQty, remove, refresh, guestToken }
  }, [items, loading, add, updateQty, remove, refresh, guestToken])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>')
  return ctx
}
